

export type PartKind =
    | 'industry'
    | 'location'
    | 'size'
    | 'revenue'
    | 'funding'
    | 'timeframe'
    | 'technology'
    | 'signal'
    | 'number';

export interface QueryPart {
    kind: PartKind;
    start: number;
    end: number;
    text: string;
}

export interface QueryCriteria {
    industries?: string[];
    locations?: string[];
    employee_ranges?: string[];
    technologies?: string[];
    buying_signals?: string[];
    keywords?: string[];
}

export type QuerySegment =
    | { type: 'text'; text: string }
    | { type: 'part'; part: QueryPart };

interface Candidate extends QueryPart {
    priority: number;
}

const DASH = '[-\\u2013\\u2014]';
const UNIT_TIME = '(?:day|week|month|quarter|year)s?';
const QUALIFIER =
    '(?:(?:over|under|above|below|more than|less than|at least|at most|up to|around|about|approximately|nearly)\\s+)?';

const COUNTRIES_AND_CITIES = [
    'India', 'United States', 'USA', 'US', 'U.S.', 'United Kingdom', 'UK', 'U.K.', 'UAE', 'United Arab Emirates',
    'Canada', 'Australia', 'Singapore', 'Germany', 'France', 'Spain', 'Italy', 'Netherlands', 'Sweden', 'Norway',
    'Denmark', 'Finland', 'Ireland', 'Switzerland', 'Brazil', 'Mexico', 'Japan', 'China', 'Hong Kong', 'Indonesia',
    'Malaysia', 'Thailand', 'Vietnam', 'Philippines', 'Saudi Arabia', 'Qatar', 'Israel', 'Egypt', 'Nigeria',
    'Kenya', 'South Africa', 'New Zealand', 'Europe', 'Asia', 'APAC', 'EMEA', 'LATAM', 'North America',
    'Middle East', 'Africa', 'Southeast Asia', 'Bangalore', 'Bengaluru', 'Mumbai', 'Delhi', 'New Delhi', 'Gurgaon',
    'Gurugram', 'Noida', 'Hyderabad', 'Chennai', 'Pune', 'Kolkata', 'Ahmedabad', 'London', 'Berlin', 'Paris',
    'Dubai', 'Abu Dhabi', 'New York', 'San Francisco', 'Los Angeles', 'Boston', 'Austin', 'Chicago', 'Seattle',
    'Toronto', 'Sydney', 'Melbourne', 'Tokyo', 'Amsterdam', 'Dublin', 'Stockholm',
];

const TECHNOLOGIES = [
    'Salesforce', 'HubSpot', 'Shopify', 'WooCommerce', 'Magento', 'AWS', 'Azure', 'GCP', 'Google Cloud', 'Snowflake',
    'Databricks', 'Stripe', 'Razorpay', 'Kubernetes', 'SAP', 'Oracle', 'Zoho', 'Workday', 'Slack', 'Zendesk',
    'Intercom', 'Marketo', 'Segment', 'Okta', 'Twilio', 'Atlassian', 'Jira', 'ServiceNow', 'NetSuite',
];

/** Plural nouns that name the kind of company; the words before one are the industry. */
const ENTITY_NOUNS = [
    'brands', 'brand', 'companies', 'company', 'startups', 'startup', 'start-ups', 'start-up', 'firms', 'firm',
    'businesses', 'business', 'agencies', 'agency', 'enterprises', 'enterprise', 'organizations', 'organisations',
    'retailers', 'manufacturers', 'providers', 'vendors', 'platforms', 'players', 'operators', 'labs', 'studios',
    'clinics', 'hospitals', 'stores', 'shops', 'marketplaces', 'developers', 'builders', 'distributors',
    'suppliers', 'consultancies', 'banks', 'lenders', 'insurers', 'publishers', 'networks', 'chains',
];

/** Words that end the industry phrase when walking backwards from the entity noun. */
const INDUSTRY_STOP_WORDS = new Set([
    'a', 'an', 'the', 'find', 'show', 'list', 'get', 'give', 'search', 'me', 'all', 'of', 'for', 'that', 'which',
    'who', 'with', 'in', 'on', 'at', 'from', 'and', 'or', 'top', 'new', 'best', 'any', 'some', 'many', 'more',
    'looking', 'want', 'need', 'i', 'we', 'us', 'our', 'my', 'to', 'is', 'are', 'by', 'near', 'around',
    'fast-growing', 'growing', 'early-stage', 'early', 'late-stage', 'funded', 'small', 'large', 'big', 'mid-size',
    'midsize', 'mid-sized', 'leading', 'emerging', 'established', 'innovative', 'profitable', 'successful',
    'popular', 'well-funded', 'bootstrapped', 'venture-backed', 'vc-backed', 'upcoming', 'major', 'local',
]);

const SIGNAL_STOP_WORDS = new Set([
    'in', 'with', 'that', 'who', 'which', 'and', 'or', 'for', 'at', 'from', 'across', 'based', 'to', 'by', 'on',
    'within', 'over', 'during', 'since', 'having', 'where', 'while',
]);

/** Built from a string so the compiler target never has to know about the "u" flag. */
const rx = (source: string, flags: string) => new RegExp(source, flags);

const escapeRegExp = (value: string) => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

/** A criteria value as a regex that tolerates dash variants and flexible spacing. */
const criterionPattern = (value: string): RegExp | null => {
    const trimmed = value.trim();
    if (trimmed.length < 2) return null;
    const source = escapeRegExp(trimmed)
        .replace(/[-–—]/g, DASH)
        .replace(/\s+/g, '\\s+');
    return new RegExp(`(?<![\\p{L}\\p{N}])${source}(?![\\p{L}\\p{N}])`, 'giu');
};

const push = (
    out: Candidate[],
    kind: PartKind,
    priority: number,
    start: number,
    end: number,
    text: string,
) => {
    const slice = text.slice(start, end);
    const lead = slice.length - slice.trimStart().length;
    const trail = slice.length - slice.trimEnd().length;
    const s = start + lead;
    const e = end - trail;
    if (e <= s) return;
    out.push({ kind, priority, start: s, end: e, text: text.slice(s, e) });
};

const collect = (out: Candidate[], re: RegExp, text: string, kind: PartKind, priority: number, group = 0) => {
    for (const match of Array.from(text.matchAll(re))) {
        if (match.index == null) continue;
        if (group === 0) {
            push(out, kind, priority, match.index, match.index + match[0].length, text);
            continue;
        }
        const captured = match[group];
        if (!captured) continue;
        const offset = match[0].indexOf(captured);
        const start = match.index + (offset < 0 ? 0 : offset);
        push(out, kind, priority, start, start + captured.length, text);
    }
};

const tokenise = (text: string) => {
    const tokens: { word: string; start: number; end: number }[] = [];
    for (const m of Array.from(text.matchAll(rx('[^\\s,;:()]+', 'gu')))) {
        if (m.index != null) tokens.push({ word: m[0], start: m.index, end: m.index + m[0].length });
    }
    return tokens;
};

/** "D2C wellness" in "D2C wellness brands in India": up to 4 words before the entity noun. */
const detectIndustryBeforeNoun = (text: string, out: Candidate[]) => {
    const tokens = tokenise(text);
    const nouns = new Set(ENTITY_NOUNS.map((n) => n.toLowerCase()));
    const lowerBare = (w: string) => w.toLowerCase().replace(/[.!?]+$/g, '');
    for (let i = 0; i < tokens.length; i += 1) {
        if (!nouns.has(lowerBare(tokens[i].word))) continue;
        let from = i;
        while (from > 0 && i - from < 4) {
            const prev = tokens[from - 1];
            if (INDUSTRY_STOP_WORDS.has(lowerBare(prev.word))) break;
            // a bare number ("top 50 companies") is a count, not part of the industry
            if (/^[\d,.]+\+?$/.test(prev.word)) break;
            // a gap with punctuation (a comma, bracket) ends the phrase
            if (/[,;:()]/.test(text.slice(prev.end, tokens[from].start))) break;
            from -= 1;
        }
        if (from < i) {
            push(out, 'industry', 2, tokens[from].start, tokens[i - 1].end, text);
        }
        break; // the first entity noun names the industry; later ones belong to other clauses
    }
};

/** "hiring RevOps" / "hiring for sales engineers": the role words after "hiring". */
const detectHiringSignal = (text: string, out: Candidate[]) => {
    const tokens = tokenise(text);
    for (let i = 0; i < tokens.length; i += 1) {
        if (tokens[i].word.toLowerCase() !== 'hiring') continue;
        let j = i + 1;
        if (tokens[j] && ['for', 'a', 'an'].includes(tokens[j].word.toLowerCase())) j += 1;
        const first = j;
        while (j < tokens.length && j - first < 4 && !SIGNAL_STOP_WORDS.has(tokens[j].word.toLowerCase())) {
            if (/[,;:()]/.test(text.slice(tokens[j - 1].end, tokens[j].start))) break;
            j += 1;
        }
        if (j > first) push(out, 'signal', 3, tokens[i].start, tokens[j - 1].end, text);
    }
};

const LOC = `(?:the\\s+)?[\\p{Lu}][\\p{L}.'-]*(?:\\s+[\\p{Lu}][\\p{L}.'-]*){0,2}`;
const LOC_LIST = `${LOC}(?:\\s*(?:,|\\band\\b|\\bor\\b|&)\\s*${LOC})*`;

export const detectParts = (text: string, criteria?: QueryCriteria | null): QueryPart[] => {
    if (!text || !text.trim()) return [];
    const found: Candidate[] = [];

    // Server-extracted criteria: matched back onto the text.
    const fromCriteria: [PartKind, string[] | undefined][] = [
        ['industry', criteria?.industries],
        ['location', criteria?.locations],
        ['size', criteria?.employee_ranges],
        ['technology', criteria?.technologies],
        ['signal', criteria?.buying_signals],
    ];
    for (const [kind, values] of fromCriteria) {
        for (const value of values ?? []) {
            const re = criterionPattern(String(value));
            if (re) collect(found, re, text, kind, 3.5);
        }
    }

    // company size: "20–200 employees", "over 500 people", "1,000+ staff"
    collect(
        found,
        new RegExp(
            `${QUALIFIER}\\d[\\d,]*(?:\\.\\d+)?\\+?\\s*(?:${DASH}|to)\\s*\\d[\\d,]*\\+?\\s*(?:employees?|people|staff|headcount|FTEs?|team members)`,
            'giu',
        ),
        text,
        'size',
        5,
    );
    collect(
        found,
        new RegExp(`${QUALIFIER}\\d[\\d,]*(?:\\.\\d+)?\\+?\\s*(?:employees?|people|staff|headcount|FTEs?|team members)`, 'giu'),
        text,
        'size',
        5,
    );

    // revenue / money: "$10M ARR", "₹50 Cr revenue", "over $5 million"
    collect(
        found,
        new RegExp(
            `(?<![\\p{L}\\p{N}])${QUALIFIER}(?:[₹$€£]|(?:USD|INR|EUR|GBP|Rs\\.?)\\s?)\\s?\\d[\\d,]*(?:\\.\\d+)?\\s*(?:k|m|mn|b|bn|cr|crores?|lakhs?|million|billion|thousand)?(?:\\s*(?:${DASH}|to)\\s*(?:[₹$€£]\\s?)?\\d[\\d,]*(?:\\.\\d+)?\\s*(?:k|m|mn|b|bn|cr|crores?|lakhs?|million|billion|thousand)?)?(?:\\s+(?:in\\s+)?(?:annual\\s+)?(?:revenue|ARR|MRR|turnover|funding))?`,
            'giu',
        ),
        text,
        'revenue',
        5,
    );

    // funding event and stage: "raised funding", "raised a Series A", "Seed stage"
    collect(
        found,
        rx("\\b(?:recently\\s+)?(?:raised|raising|secured|closed|received)\\s+(?:(?:a|an|new)\\s+)?(?:pre-?seed|seed|series\\s+[a-f]\\+?|funding|investment|capital|round)?(?:\\s+(?:round|funding|investment))?", 'giu'),
        text,
        'funding',
        4.5,
    );
    collect(found, rx("\\b(?:pre-?seed|seed|series\\s+[a-f]\\+?|bootstrapped|vc-backed|venture-backed)\\b", 'giu'), text, 'funding', 4);

    // timeframe: "last 6 months", "this year", "Q3 2026", "in 2025"
    collect(found, new RegExp(`\\b(?:last|past|previous|next|recent)\\s+\\d+\\s*${UNIT_TIME}\\b`, 'giu'), text, 'timeframe', 4.5);
    collect(found, rx("\\b(?:this|last|next|past)\\s+(?:year|quarter|month|week)\\b", 'giu'), text, 'timeframe', 4);
    collect(found, rx("\\bQ[1-4]\\s*(?:(?:19|20)?\\d{2})?\\b", 'gu'), text, 'timeframe', 4);
    collect(
        found,
        rx("(?<=\\b(?:in|since|by|during|before|after|from)\\s)(?:19|20)\\d{2}\\b", 'giu'),
        text,
        'timeframe',
        4,
    );

    // location: "in India", "based in the UK", "across Germany, France and Spain", "UK-based"
    collect(
        found,
        new RegExp(`\\b(?:based in|headquartered in|located in|operating in|across|in|from|within)\\s+(${LOC_LIST})`, 'gu'),
        text,
        'location',
        3,
        1,
    );
    collect(
        found,
        new RegExp(
            `\\b(?:based in|headquartered in|located in|operating in|across|in|from|within)\\s+((?:${COUNTRIES_AND_CITIES.map(escapeRegExp).join('|')})(?:\\s*(?:,|\\band\\b|\\bor\\b|&)\\s*(?:${COUNTRIES_AND_CITIES.map(escapeRegExp).join('|')}))*)(?![\\p{L}])`,
            'giu',
        ),
        text,
        'location',
        3,
        1,
    );
    collect(found, rx("\\b([\\p{Lu}][\\p{L}]+(?:\\s[\\p{Lu}][\\p{L}]+)?)-based\\b", 'gu'), text, 'location', 3, 1);

    // technology: "using Salesforce"
    collect(
        found,
        new RegExp(`(?<![\\p{L}\\p{N}])(?:${TECHNOLOGIES.map(escapeRegExp).join('|')})(?![\\p{L}\\p{N}])`, 'giu'),
        text,
        'technology',
        3,
    );

    detectHiringSignal(text, found);

    // industry: "in the fintech industry"
    collect(
        found,
        rx("\\b(?:in|within)\\s+(?:the\\s+)?([\\p{L}0-9&+/' -]{2,40}?)\\s+(?:industry|sector|space|vertical|market)\\b", 'giu'),
        text,
        'industry',
        2.5,
        1,
    );
    detectIndustryBeforeNoun(text, found);

    // any other standalone number: "top 50 companies"
    collect(found, rx("(?<![\\p{L}\\p{N}.])\\d[\\d,]*(?:\\.\\d+)?\\+?(?![\\p{L}\\p{N}])", 'gu'), text, 'number', 1);

    // Resolve overlaps: higher priority first, then the longer span.
    found.sort((a, b) => b.priority - a.priority || b.end - b.start - (a.end - a.start) || a.start - b.start);
    const accepted: Candidate[] = [];
    for (const candidate of found) {
        const clashes = accepted.some((p) => candidate.start < p.end && candidate.end > p.start);
        if (!clashes) accepted.push(candidate);
    }
    return accepted
        .sort((a, b) => a.start - b.start)
        .map(({ kind, start, end, text: partText }) => ({ kind, start, end, text: partText }));
};

/** The query as alternating plain text and editable parts, in reading order. */
export const segmentQuery = (text: string, criteria?: QueryCriteria | null): QuerySegment[] => {
    const parts = detectParts(text, criteria);
    const segments: QuerySegment[] = [];
    let cursor = 0;
    for (const part of parts) {
        if (part.start > cursor) segments.push({ type: 'text', text: text.slice(cursor, part.start) });
        segments.push({ type: 'part', part });
        cursor = part.end;
    }
    if (cursor < text.length) segments.push({ type: 'text', text: text.slice(cursor) });
    return segments;
};

const tidy = (value: string) =>
    value
        .replace(/\s{2,}/g, ' ')
        .replace(/\s+([,.;:!?])/g, '$1')
        .replace(/^[\s,;:]+/, '')
        .trim();

const DANGLING_CONNECTOR =
    /\s*\b(?:in|with|that|who|which|having|based in|located in|headquartered in|operating in|from|across|within|of|using|and|or|the|over|during|since|by)\s*$/i;

/** Swap one part for new text; every other character of the query is left exactly as typed. */
export const replacePart = (text: string, part: QueryPart, value: string): string => {
    const next = value.trim();
    if (!next) return removePart(text, part);
    return tidy(text.slice(0, part.start) + next + text.slice(part.end));
};

/** Drop one part, along with the connector word ("in", "with", "that") that introduced it. */
export const removePart = (text: string, part: QueryPart): string => {
    let before = text.slice(0, part.start);
    const after = text.slice(part.end);
    let previous: string;
    do {
        previous = before;
        before = before.replace(DANGLING_CONNECTOR, '');
    } while (before !== previous && before.trim());
    return tidy(`${before} ${after}`);
};

/** Short, kind-specific starting points shown under the input. */
export const SUGGESTIONS: Record<PartKind, string[]> = {
    industry: ['SaaS', 'Fintech', 'Healthcare', 'E-commerce', 'Manufacturing', 'Real estate developers', 'EdTech', 'Logistics'],
    location: ['India', 'United States', 'United Kingdom', 'UAE', 'Singapore', 'Germany', 'Canada', 'Australia'],
    size: ['1-10 employees', '11-50 employees', '51-200 employees', '201-500 employees', '501-1000 employees', '1000+ employees'],
    revenue: ['$1M ARR', '$5M ARR', '$10M ARR', '$50M revenue', '$100M revenue'],
    funding: ['raised funding', 'raised a Seed round', 'raised a Series A', 'raised a Series B', 'bootstrapped'],
    timeframe: ['last 30 days', 'last 3 months', 'last 6 months', 'last 12 months', 'last 2 years'],
    technology: ['Salesforce', 'HubSpot', 'Shopify', 'AWS', 'Snowflake', 'Stripe'],
    signal: ['hiring sales reps', 'hiring RevOps', 'hiring engineers', 'hiring marketing managers'],
    number: ['10', '25', '50', '100', '500'],
};

export const PART_LABELS: Record<PartKind, string> = {
    industry: 'Industry',
    location: 'Location',
    size: 'Company size',
    revenue: 'Revenue',
    funding: 'Funding',
    timeframe: 'Timeframe',
    technology: 'Technology',
    signal: 'Hiring / signal',
    number: 'Number',
};
